// frontend/src/components/staff/ChangeHistoryDrawer.js
//
// Workforce — TIME-010 §6.5 Change History drawer.
//
// Reads `manualAdjustments[]` directly off the TimeLog response (no separate
// endpoint per Marcus tech doc §15 change-log note). Reverse-chronological
// list of admin edits + staff-request approvals + (future) auto-clock-out
// entries (TIME-007 forward-compat).
//
// RBAC posture (Zuri spec §6.5):
//   - Visible to admin (any TimeLog) and staff (own TimeLogs only).
//   - The `[Show full snapshot]` accordion is RBAC-gated CLIENT-SIDE in v1.
//     Hidden for staff role; visible for admin role. Marcus §10.2 item 3
//     defaulted no contract change here.
//
// Type-chip variants (Zuri spec §6.5):
//   admin_edit             — purple --status-locked (#553C9A)
//   staff_request_approval — gold --brand-gold (#B28F61) +
//                            [View original request →] link
//   system_auto_clock_out  — neutral, TIME-007 forward-compat
//   null / legacy          — neutral --text-secondary fallback

import React, { useState } from "react";
import moment from "moment-timezone";
import "./ChangeHistoryDrawer.css";

const STUDIO_TZ = "America/Phoenix";

const formatStudioDateTime = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return `${m.format("YYYY-MM-DD h:mm A")} MST`;
};

const formatStudioTime = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return `${m.format("h:mm A")} MST`;
};

const TYPE_META = {
  admin_edit: { label: "Admin edit", className: "chd-chip-admin" },
  staff_request_approval: {
    label: "Staff request approval",
    className: "chd-chip-staff",
  },
  system_auto_clock_out: {
    label: "Auto-clock-out",
    className: "chd-chip-system",
  },
};

const FALLBACK_META = { label: "Edit", className: "chd-chip-fallback" };

/**
 * Render a single field-level diff line. Handles the common shape
 * `{ punchInTime: 'iso', breaks: [...] }` for both oldValue + newValue.
 */
const renderDiffPair = (label, oldVal, newVal, formatter = (v) => String(v)) => (
  <div className="chd-diff-line">
    <span className="chd-diff-label">{label}:</span>{" "}
    <span className="chd-diff-old">{formatter(oldVal)}</span>{" "}
    <span aria-hidden="true">&rarr;</span>{" "}
    <span className="chd-diff-new">{formatter(newVal)}</span>
  </div>
);

const renderBreaks = (breaks) => {
  if (!Array.isArray(breaks) || breaks.length === 0) return "(none)";
  return breaks
    .map(
      (b) =>
        `${formatStudioTime(b.startTime)} – ${formatStudioTime(
          b.endTime
        )} ${b.isPaid ? "(paid)" : "(unpaid)"}`
    )
    .join("; ");
};

const ChangeHistoryDrawer = ({
  open,
  timeLog,
  isAdmin,
  staffNameById,
  onClose,
  onViewOriginalRequest,
}) => {
  const [snapshotExpanded, setSnapshotExpanded] = useState({});

  if (!open || !timeLog) return null;

  const adjustments = Array.isArray(timeLog.manualAdjustments)
    ? [...timeLog.manualAdjustments]
    : [];
  // Reverse-chronological: server appends in order, so reverse the array for
  // display.
  adjustments.reverse();

  const dateLabel = timeLog.punchInTime
    ? moment.tz(timeLog.punchInTime, STUDIO_TZ).format("ddd, MMM D, YYYY")
    : "—";

  return (
    <>
      <div
        className="chd-backdrop"
        onClick={onClose}
        aria-hidden="true"
        data-testid="chd-backdrop"
      />
      <aside
        className="chd-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chd-heading"
        data-testid="chd-drawer"
      >
        <header className="chd-header">
          <div>
            <h2 id="chd-heading" className="chd-heading">
              Change history
            </h2>
            <p className="chd-subline">{dateLabel}</p>
          </div>
          <button
            type="button"
            className="chd-close"
            onClick={onClose}
            aria-label="Close change history"
            data-testid="chd-close"
          >
            &times;
          </button>
        </header>

        <div className="chd-body">
          {adjustments.length === 0 ? (
            <p className="chd-empty">No changes have been recorded.</p>
          ) : (
            <ul className="chd-list">
              {adjustments.map((adj, i) => {
                const meta = TYPE_META[adj.type] || FALLBACK_META;
                const adminName =
                  (staffNameById && staffNameById.get(adj.modifiedBy)) ||
                  "Admin";
                const isExpanded = Boolean(snapshotExpanded[i]);
                const oldVal = adj.oldValue || {};
                const newVal = adj.newValue || {};
                return (
                  <li className="chd-card" key={i}>
                    <header className="chd-card-header">
                      <span className={`chd-chip ${meta.className}`}>
                        <span aria-hidden="true" className="chd-dot" />
                        {meta.label}
                      </span>
                      <div className="chd-card-meta">
                        <div className="chd-actor">
                          Modified by {adminName}
                        </div>
                        <div className="chd-when">
                          {formatStudioDateTime(adj.modifiedAt)}
                        </div>
                      </div>
                    </header>

                    {adj.reason && (
                      <blockquote className="chd-reason">
                        &ldquo;{adj.reason}&rdquo;
                      </blockquote>
                    )}

                    {adj.type === "staff_request_approval" &&
                      adj.correctionRequestId && (
                        <button
                          type="button"
                          className="chd-original-link"
                          onClick={() =>
                            typeof onViewOriginalRequest === "function" &&
                            onViewOriginalRequest(adj.correctionRequestId)
                          }
                          data-testid={`chd-view-original-${i}`}
                        >
                          View original request &rarr;
                        </button>
                      )}

                    <div className="chd-changes">
                      <div className="chd-changes-heading">Changes:</div>
                      {(oldVal.punchInTime || newVal.punchInTime) &&
                        oldVal.punchInTime !== newVal.punchInTime &&
                        renderDiffPair(
                          "Punch in",
                          oldVal.punchInTime,
                          newVal.punchInTime,
                          formatStudioTime
                        )}
                      {(oldVal.punchOutTime || newVal.punchOutTime) &&
                        oldVal.punchOutTime !== newVal.punchOutTime &&
                        renderDiffPair(
                          "Punch out",
                          oldVal.punchOutTime,
                          newVal.punchOutTime,
                          formatStudioTime
                        )}
                      {(oldVal.jobCode || newVal.jobCode) &&
                        oldVal.jobCode !== newVal.jobCode &&
                        renderDiffPair(
                          "Job code",
                          oldVal.jobCode || "(none)",
                          newVal.jobCode || "(none)"
                        )}
                      {(oldVal.breaks || newVal.breaks) &&
                        renderDiffPair(
                          "Breaks",
                          oldVal.breaks,
                          newVal.breaks,
                          renderBreaks
                        )}
                      {/* For creation-type entries, signal it explicitly. */}
                      {adj.field === "creation" && (
                        <div className="chd-diff-line">
                          <span className="chd-diff-label">Created</span>{" "}
                          via {meta.label.toLowerCase()}.
                        </div>
                      )}
                    </div>

                    {isAdmin && (adj.beforeSnapshot || adj.afterSnapshot) && (
                      <div className="chd-snapshot">
                        <button
                          type="button"
                          className="chd-snapshot-toggle"
                          onClick={() =>
                            setSnapshotExpanded((p) => ({
                              ...p,
                              [i]: !p[i],
                            }))
                          }
                          aria-expanded={isExpanded}
                          data-testid={`chd-snapshot-toggle-${i}`}
                        >
                          {isExpanded
                            ? "Hide full snapshot"
                            : "Show full snapshot"}
                        </button>
                        {isExpanded && (
                          <div className="chd-snapshot-body">
                            {adj.beforeSnapshot && (
                              <details className="chd-snapshot-pair" open>
                                <summary>Before</summary>
                                <pre className="chd-snapshot-json">
                                  {typeof adj.beforeSnapshot === "string"
                                    ? adj.beforeSnapshot
                                    : JSON.stringify(
                                        adj.beforeSnapshot,
                                        null,
                                        2
                                      )}
                                </pre>
                              </details>
                            )}
                            {adj.afterSnapshot && (
                              <details className="chd-snapshot-pair" open>
                                <summary>After</summary>
                                <pre className="chd-snapshot-json">
                                  {typeof adj.afterSnapshot === "string"
                                    ? adj.afterSnapshot
                                    : JSON.stringify(
                                        adj.afterSnapshot,
                                        null,
                                        2
                                      )}
                                </pre>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
};

export default ChangeHistoryDrawer;
